import {
  collection,
  query,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  onSnapshot,
  where,
  orderBy,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '../firebase/firebase';
import { Merchant } from '../models/Merchant';
import { MerchantProduct } from '../models/MerchantProduct';
import { MerchantOrder } from '../models/MerchantOrder';
import { ConsolidatedOrder } from '../models/ConsolidatedOrder';
import userService from './user.service';

class MerchantOrdersService {
  constructor() {
    this.merchantsCollection = 'merchants';
    this.productsCollection = 'merchantProducts';
    this.ordersCollection = 'merchantOrders';
    this.consolidatedCollection = 'consolidatedOrders';
    this.unsubscribe = null;
  }

  // ==================== MERCHANTS ====================

  async getMerchants() {
    const collectionRef = collection(db, this.merchantsCollection);
    const q = query(collectionRef, orderBy('name'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => Merchant.fromFirebase(doc));
  }

  async addMerchant(name, phone = '', notes = '') {
    const collectionRef = collection(db, this.merchantsCollection);
    const merchant = new Merchant({
      id: null,
      name,
      phone,
      notes,
      createdAt: serverTimestamp(),
    });
    const docRef = await addDoc(collectionRef, merchant.toJson());
    merchant.id = docRef.id;
    return merchant;
  }

  async updateMerchant(id, data) {
    const docRef = doc(db, this.merchantsCollection, id);
    await updateDoc(docRef, data);
  }

  subscribeMerchants(callback) {
    const collectionRef = collection(db, this.merchantsCollection);
    const q = query(collectionRef, orderBy('name'));
    return onSnapshot(q, (snapshot) => {
      const merchants = snapshot.docs.map(doc => Merchant.fromFirebase(doc));
      callback(merchants);
    }, (error) => {
      console.error('Error listening to merchants:', error);
      callback([]);
    });
  }

  // ==================== PRODUCTS ====================

  async getProducts(merchantId) {
    const collectionRef = collection(db, this.productsCollection);
    const q = query(
      collectionRef,
      where('merchantId', '==', merchantId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(doc => MerchantProduct.fromFirebase(doc))
      .filter(p => p.active)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  async getAllProducts() {
    const collectionRef = collection(db, this.productsCollection);
    const snapshot = await getDocs(collectionRef);
    return snapshot.docs
      .map(doc => MerchantProduct.fromFirebase(doc))
      .filter(p => p.active)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  subscribeAllProducts(callback) {
    const collectionRef = collection(db, this.productsCollection);
    return onSnapshot(collectionRef, (snapshot) => {
      const products = snapshot.docs
        .map(doc => MerchantProduct.fromFirebase(doc))
        .filter(p => p.active)
        .sort((a, b) => a.name.localeCompare(b.name));
      callback(products);
    }, (error) => {
      console.error('Error listening to all products:', error);
      callback([]);
    });
  }

  async addProduct(merchantId, name, orderUnit, pricingUnit, pricePerPricingUnit, estimatedConversion = null) {
    const collectionRef = collection(db, this.productsCollection);
    const product = new MerchantProduct({
      id: null,
      merchantId,
      name,
      orderUnit,
      pricingUnit,
      pricePerPricingUnit,
      estimatedConversion,
      active: true,
      createdAt: serverTimestamp(),
    });
    const docRef = await addDoc(collectionRef, product.toJson());
    product.id = docRef.id;
    return product;
  }

  async updateProduct(id, { name, orderUnit, pricingUnit, pricePerPricingUnit, estimatedConversion }) {
    const docRef = doc(db, this.productsCollection, id);
    await updateDoc(docRef, {
      name,
      orderUnit,
      pricingUnit,
      pricePerPricingUnit,
      estimatedConversion: orderUnit !== pricingUnit ? estimatedConversion : null,
      lastUpdatedAt: serverTimestamp(),
    });
  }

  async deactivateProduct(id) {
    const docRef = doc(db, this.productsCollection, id);
    await updateDoc(docRef, { active: false });
  }

  subscribeProducts(merchantId, callback) {
    const collectionRef = collection(db, this.productsCollection);
    const q = query(
      collectionRef,
      where('merchantId', '==', merchantId)
    );
    return onSnapshot(q, (snapshot) => {
      const products = snapshot.docs
        .map(doc => MerchantProduct.fromFirebase(doc))
        .filter(p => p.active)
        .sort((a, b) => a.name.localeCompare(b.name));
      callback(products);
    }, (error) => {
      console.error('Error listening to products:', error);
      callback([]);
    });
  }

  // ==================== STORE ORDERS ====================

  async createOrder(merchantId, merchantName, items, notes = '') {
    const storeId = await userService.getUserStoreId();
    const userId = await userService.getCurrentUserId();
    const total = MerchantOrder.calculateTotal(items);

    const collectionRef = collection(db, this.ordersCollection);
    const order = new MerchantOrder({
      id: null,
      merchantId,
      merchantName,
      store_id: storeId,
      status: 'pending',
      consolidatedOrderId: null,
      items,
      total,
      notes,
      createdBy: userId,
      createdAt: serverTimestamp(),
      receivedAt: null,
    });

    const docRef = await addDoc(collectionRef, order.toJson());
    order.id = docRef.id;
    return order;
  }

  async createOrdersFromCart(items, merchants, notes = '') {
    // Group items by merchantId
    const itemsByMerchant = {};
    items.forEach(item => {
      if (!itemsByMerchant[item.merchantId]) {
        itemsByMerchant[item.merchantId] = [];
      }
      itemsByMerchant[item.merchantId].push(item);
    });

    // Create one order per merchant
    const orders = [];
    for (const merchantId of Object.keys(itemsByMerchant)) {
      const merchantItems = itemsByMerchant[merchantId];
      const merchant = merchants.find(m => m.id === merchantId);
      const merchantName = merchant ? merchant.name : 'Unknown Merchant';
      const order = await this.createOrder(merchantId, merchantName, merchantItems, notes);
      orders.push(order);
    }

    return orders;
  }

  async getOrders(statusFilter = null) {
    const storeId = await userService.getUserStoreId();
    const collectionRef = collection(db, this.ordersCollection);

    let q;
    if (statusFilter) {
      q = query(
        collectionRef,
        where('store_id', '==', storeId),
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collectionRef,
        where('store_id', '==', storeId),
        orderBy('createdAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => MerchantOrder.fromFirebase(doc));
  }

  subscribeOrders(callback, statusFilter = null) {
    const self = this;

    (async () => {
      try {
        const storeId = await userService.getUserStoreId();
        if (!storeId) {
          console.warn('No store ID found for user');
          callback([]);
          return;
        }

        const collectionRef = collection(db, self.ordersCollection);

        let q;
        if (statusFilter) {
          q = query(
            collectionRef,
            where('store_id', '==', storeId),
            where('status', '==', statusFilter),
            orderBy('createdAt', 'desc')
          );
        } else {
          q = query(
            collectionRef,
            where('store_id', '==', storeId),
            orderBy('createdAt', 'desc')
          );
        }

        self.unsubscribe = onSnapshot(q, (snapshot) => {
          const orders = snapshot.docs.map(doc => MerchantOrder.fromFirebase(doc));
          callback(orders);
        }, (error) => {
          console.error('Error listening to orders:', error);
          callback([]);
        });
      } catch (error) {
        console.error('Error setting up orders subscription:', error);
        callback([]);
      }
    })();

    return () => {
      if (self.unsubscribe) {
        self.unsubscribe();
      }
    };
  }

  async markReceived(orderId) {
    const docRef = doc(db, this.ordersCollection, orderId);
    await updateDoc(docRef, {
      status: 'received',
      receivedAt: serverTimestamp(),
    });
  }

  /**
   * Update an order with arbitrary fields (e.g., editRequest)
   */
  async updateOrder(orderId, data) {
    const docRef = doc(db, this.ordersCollection, orderId);
    await updateDoc(docRef, {
      ...data,
      lastUpdatedAt: serverTimestamp(),
    });
  }

  // ==================== HQ CONSOLIDATION ====================

  async getAllPendingOrders() {
    const collectionRef = collection(db, this.ordersCollection);
    const q = query(
      collectionRef,
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => MerchantOrder.fromFirebase(doc));
  }

  async getPendingOrdersByMerchant(merchantId) {
    const collectionRef = collection(db, this.ordersCollection);
    const q = query(
      collectionRef,
      where('status', '==', 'pending'),
      where('merchantId', '==', merchantId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => MerchantOrder.fromFirebase(doc));
  }

  async consolidateOrders(merchantId, merchantName, orderIds) {
    const userId = await userService.getCurrentUserId();
    const batch = writeBatch(db);

    // Fetch the orders to consolidate
    const orders = [];
    for (const orderId of orderIds) {
      const collectionRef = collection(db, this.ordersCollection);
      const q = query(collectionRef, where('__name__', '==', orderId));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        orders.push(MerchantOrder.fromFirebase(snapshot.docs[0]));
      }
    }

    // Aggregate items
    const aggregatedItems = ConsolidatedOrder.aggregateItems(orders);
    const total = aggregatedItems.reduce((sum, item) => sum + item.total, 0);

    // Create consolidated order
    const consolidatedRef = doc(collection(db, this.consolidatedCollection));
    const consolidated = new ConsolidatedOrder({
      id: consolidatedRef.id,
      merchantId,
      merchantName,
      status: 'pending',
      storeOrderIds: orderIds,
      items: aggregatedItems,
      total,
      createdBy: userId,
      createdAt: serverTimestamp(),
      orderedAt: null,
      receivedAt: null,
    });
    batch.set(consolidatedRef, consolidated.toJson());

    // Update store orders to consolidated status
    for (const orderId of orderIds) {
      const orderRef = doc(db, this.ordersCollection, orderId);
      batch.update(orderRef, {
        status: 'consolidated',
        consolidatedOrderId: consolidatedRef.id,
      });
    }

    await batch.commit();
    return consolidated;
  }

  async getConsolidatedOrders(statusFilter = null) {
    const collectionRef = collection(db, this.consolidatedCollection);

    let q;
    if (statusFilter) {
      q = query(
        collectionRef,
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(collectionRef, orderBy('createdAt', 'desc'));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ConsolidatedOrder.fromFirebase(doc));
  }

  subscribeConsolidatedOrders(callback, statusFilter = null) {
    const collectionRef = collection(db, this.consolidatedCollection);

    let q;
    if (statusFilter) {
      q = query(
        collectionRef,
        where('status', '==', statusFilter),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(collectionRef, orderBy('createdAt', 'desc'));
    }

    return onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map(doc => ConsolidatedOrder.fromFirebase(doc));
      callback(orders);
    });
  }

  async markConsolidatedOrdered(consolidatedId) {
    const batch = writeBatch(db);

    // Update consolidated order
    const consolidatedRef = doc(db, this.consolidatedCollection, consolidatedId);
    batch.update(consolidatedRef, {
      status: 'ordered',
      orderedAt: serverTimestamp(),
    });

    // Update all linked store orders
    const collectionRef = collection(db, this.ordersCollection);
    const q = query(collectionRef, where('consolidatedOrderId', '==', consolidatedId));
    const snapshot = await getDocs(q);

    snapshot.docs.forEach(docSnap => {
      batch.update(docSnap.ref, { status: 'ordered' });
    });

    await batch.commit();
  }

  async markConsolidatedReceived(consolidatedId) {
    const batch = writeBatch(db);

    // Update consolidated order
    const consolidatedRef = doc(db, this.consolidatedCollection, consolidatedId);
    batch.update(consolidatedRef, {
      status: 'received',
      receivedAt: serverTimestamp(),
    });

    // Update all linked store orders
    const collectionRef = collection(db, this.ordersCollection);
    const q = query(collectionRef, where('consolidatedOrderId', '==', consolidatedId));
    const snapshot = await getDocs(q);

    snapshot.docs.forEach(docSnap => {
      batch.update(docSnap.ref, {
        status: 'received',
        receivedAt: serverTimestamp(),
      });
    });

    await batch.commit();
  }

  stopListening() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
}

const merchantOrdersService = new MerchantOrdersService();
export default merchantOrdersService;
